import { marker } from "@jsverse/transloco-keys-manager/marker";
import { lastValueFrom } from "rxjs";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { ReminderBackend } from "src/app/backend/api/reminder.backend";
import { Pet } from "src/app/backend/models/pet/pet";
import {
  Reminder,
  ReminderCalendarRecipient,
  ReminderRecipient
} from "src/app/backend/models/reminder";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import {
  DocumentDataModel,
  DocumentService
} from "src/app/services/document.service";
import { ToastService } from "src/app/services/toast.service";
import {
  replaceHTMLBRWithNewLine,
  stripHTMLTags,
  trimStarEndingNewLines
} from "src/app/support/string";
import { sntTranslate } from "src/app/support/translate";
import { openWhatsapp } from "src/app/support/whatsapp";
import { dateUtil } from "src/app/support/date-util";

type ReminderSendWhatsAppFunctionInput = {
  reminder: Reminder;
  recipient: ReminderRecipient;
  patient?: Pet;
};

type ReminderSendWhatsAppCalendarFunctionInput = {
  templateMessage: string;
  recipient: ReminderCalendarRecipient;
  patientId?: string;
};

type ReminderSendWhatsAppFunctionProviders = {
  toastService: ToastService;
  customerBackend: CustomerBackend;
  patientBackend: PetBackend;
  reminderBackend: ReminderBackend;
  documentService: DocumentService;
  bootstrapService: BootstrapService;
};

export async function reminderSendWhatsApp(
  input: ReminderSendWhatsAppFunctionInput,
  providers: ReminderSendWhatsAppFunctionProviders
): Promise<void> {
  await providers.toastService.observe(
    async () => {
      if (!input.recipient) {
        providers.toastService.warning(
          sntTranslate(marker("Reminder senza destinatario"))
        );
        return;
      }

      const customer = await lastValueFrom(
        providers.customerBackend.load(input.recipient.id)
      );

      if (!customer.phone) {
        providers.toastService.warning(
          sntTranslate(
            marker(
              "Inserisci il numero del cliente per poter proseguire all'invio su WhatsApp"
            )
          )
        );
        return;
      }

      const renderResult =
        await providers.documentService.renderWithMustacheProperties(
          input.reminder.body!,
          async () => {
            return <DocumentDataModel>{
              customer: customer,
              patient: input.patient,
              tenant: providers.bootstrapService.getCurrentTenant(),
              reminder: {
                name: "",
                expiresAt: input.reminder.expiresAt,
                scheduledAt: input.reminder.scheduledAt
              }
            };
          }
        );

      const sanitized = input.reminder.body
        ? trimStarEndingNewLines(
            stripHTMLTags(replaceHTMLBRWithNewLine(renderResult))
          )
        : "";

      await openWhatsapp(customer.phone, sanitized, providers.bootstrapService);

      await lastValueFrom(providers.reminderBackend.send(input.reminder.id));
    },
    {
      success: sntTranslate(marker("Aperto")),
      loading: sntTranslate(marker("Collegamento a Whatsapp")),
      error: sntTranslate(marker("Impossibile aprire WhatsApp"))
    }
  );
}

export async function reminderSendWhatsAppFromCalendar(
  input: ReminderSendWhatsAppCalendarFunctionInput,
  providers: ReminderSendWhatsAppFunctionProviders
): Promise<void> {
  await providers.toastService.observe(
    async () => {
      if (!input.recipient) {
        providers.toastService.warning(
          sntTranslate(marker("Reminder senza destinatario"))
        );
        return;
      }

      const customer = input.recipient.customerId
        ? await lastValueFrom(
            providers.customerBackend.load(input.recipient.customerId)
          )
        : { firstaname: "Cliente" };

      const patient = input.patientId
        ? await lastValueFrom(
            providers.patientBackend.load({ id: input.patientId })
          )
        : undefined;

      const renderResult =
        await providers.documentService.renderWithMustacheProperties(
          input.templateMessage!,
          async () => {
            return <DocumentDataModel>{
              customer: customer,
              patient: patient,
              tenant: providers.bootstrapService.getCurrentTenant(),
              reminder: {
                name: "",
                expiresAt: dateUtil().toISOString(),
                scheduledAt: dateUtil().toISOString()
              }
            };
          }
        );

      const sanitized = input.templateMessage
        ? trimStarEndingNewLines(
            stripHTMLTags(replaceHTMLBRWithNewLine(renderResult))
          )
        : "";

      await openWhatsapp(
        input.recipient.phone,
        sanitized,
        providers.bootstrapService
      );
    },
    {
      success: sntTranslate(marker("Aperto")),
      loading: sntTranslate(marker("Collegamento a Whatsapp")),
      error: sntTranslate(marker("Impossibile aprire WhatsApp"))
    }
  );
}
